export type FormBuilderWidget =
  | 'text'
  | 'textarea'
  | 'number'
  | 'date'
  | 'checkbox'
  | 'select'
  | 'radio'
  | 'autocomplete'
  | 'html_content'
  | 'image'
  | 'section_header';

// Widgets that carry no answer — display-only content interleaved between
// real fields. Excluded from x-ui.order's required set, yup validation,
// and form_data.
export const CONTENT_WIDGETS: FormBuilderWidget[] = ['html_content', 'image', 'section_header'];

export type AutocompleteModel = 'staff' | 'person' | 'organization' | 'program' | 'phase_type';

export interface FieldOption {
  value: string;
  label: string;
}

export interface FieldDef {
  key: string;
  label: string;
  widget: FormBuilderWidget;
  required: boolean;
  multiple?: boolean;
  options?: FieldOption[];
  allowOther?: boolean;
  model?: AutocompleteModel;
  placeholder?: string;
  description?: string;
  // html_content
  body?: string;
  // image
  url?: string;
  // The question this field resolves to. Every saved field has one — the
  // backend mints a private question for a field that arrives without a link —
  // so this says nothing about whether the question is shared.
  questionId?: string;
  // True only when that question lives in the shared library, which is what
  // the builder means by "reusable".
  inLibrary?: boolean;
  // The form-local section this field belongs to, if any.
  sectionKey?: string;
}

// A section placed in the form: a group of fields, optionally sourced from a
// shared library section (sectionId).
export interface SectionMeta {
  key: string;
  title: string;
  sectionId?: string;
}

export interface FieldUi {
  widget: FormBuilderWidget;
  options?: FieldOption[];
  allowOther?: boolean;
  multiple?: boolean;
  model?: AutocompleteModel;
  placeholder?: string;
  description?: string;
  body?: string;
  url?: string;
  // Links a field to the question it resolves to and to its section, mirrored
  // by the backend. `inLibrary` marks that question as shared rather than a
  // form's private one. Ignored by JSON Schema validators (x-* extension keys).
  questionId?: string;
  inLibrary?: boolean;
  sectionKey?: string;
}

// A JSON Schema (draft 2020-12) subset. `x-ui` keys are spec-legal extension
// keywords ignored by validators.
export interface FormSchemaProperty {
  type?: string;
  format?: string;
  title?: string;
  enum?: string[];
  anyOf?: unknown[];
  items?: unknown;
  properties?: Record<string, unknown>;
  required?: string[];
  additionalProperties?: boolean;
  'x-ui': FieldUi;
}

export interface SchemaSection {
  key: string;
  title: string;
  sectionId?: string;
}

export interface FormSchema {
  type: 'object';
  properties: Record<string, FormSchemaProperty>;
  required: string[];
  additionalProperties: false;
  'x-ui': { order: string[]; sections?: SchemaSection[] };
}

// Shapes returned by the /forms/questions and /forms/sections library APIs.
export interface LibraryQuestion {
  id: string;
  key: string;
  text: string;
  definition: FormSchemaProperty;
}

export interface LibrarySectionQuestion {
  // The member question's own key, which becomes its JSON key in a form.
  key: string;
  required: boolean;
  definition: FormSchemaProperty;
}

export interface LibrarySection {
  id: string;
  key: string;
  title: string;
  questions: LibrarySectionQuestion[];
}

export interface AutocompleteValue {
  id: string;
  // Optional: the schema only requires `id`, so a stored value may lack a
  // label (e.g. captured before a label was available). Fall back to `id`.
  label?: string;
}

export type FormFieldValue =
  string | string[] | number | boolean | AutocompleteValue | AutocompleteValue[] | null | undefined;

export type FormDataValues = Record<string, FormFieldValue>;
